import React from 'react';
import { useDarkClasses } from '../../../../hooks/useDarkClasses';
import { CONTEMPORARY_MODULE_DATA } from '../genreModuleData';
import { SearchableChipField, SingleSelectField } from './shared';
const ACCENT = 'bg-emerald-100 text-emerald-700 border-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-300';
// ═══════════════════════════════════════════════════
// FULL CONTEMPORARY MODULE
// ═══════════════════════════════════════════════════
interface ContemporaryModuleProps {
  subtype: string;
  setSubtype: (v: string) => void;
  conflictTypes: string[];
  setConflictTypes: React.Dispatch<React.SetStateAction<string[]>>;
  storyEngines: string[];
  setStoryEngines: React.Dispatch<React.SetStateAction<string[]>>;
  emotionalWeight: string;
  setEmotionalWeight: (v: string) => void;
}
export const ContemporaryModule: React.FC<ContemporaryModuleProps> = ({
  subtype,
  setSubtype,
  conflictTypes,
  setConflictTypes,
  storyEngines,
  setStoryEngines,
  emotionalWeight,
  setEmotionalWeight
}) => {
  const dk = useDarkClasses();
  const toggleConflict = (c: string) => setConflictTypes(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const toggleEngine = (e: string) => setStoryEngines(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  return <div className="space-y-8" data-id="element-21249" data-mp="3daeheutijhdp">
      {/* Subtype */}
      <SingleSelectField label="Subtype" placeholder="e.g. Coming of Age, Family Drama, Slice of Life..." items={CONTEMPORARY_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-21250" data-mp="3r2ul7xu8njfo" />

      {/* Conflict Type */}
      <SearchableChipField label="Conflict Type" placeholder="e.g. Man vs Self, Society, Nature..." items={CONTEMPORARY_MODULE_DATA.conflictTypes} selected={conflictTypes} onToggle={toggleConflict} accentClass={ACCENT} data-id="element-21251" data-mp="3w2qh19ss84k5" />

      {/* Story Engine */}
      <SearchableChipField label="Story Engine" placeholder="e.g. Finding Oneself, Starting Over, Secret Revealed..." items={CONTEMPORARY_MODULE_DATA.storyEngines} selected={storyEngines} onToggle={toggleEngine} accentClass={ACCENT} data-id="element-21252" data-mp="5luib1atfghu" />

      {/* Emotional Weight */}
      <div data-id="element-21253" data-mp="jm59k5g1ex3j">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21254" data-mp="jlvi0dfcghvq">
          Emotional Weight
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-21255" data-mp="oi2rgg6y8bif">
          {CONTEMPORARY_MODULE_DATA.emotionalWeights.map(ew => {
          const isSelected = emotionalWeight === ew.level;
          return <button key={ew.level} onClick={() => setEmotionalWeight(isSelected ? '' : ew.level)} className={`p-3 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-emerald-600 text-white border-emerald-600 dark:bg-emerald-400 dark:text-emerald-950' : `${dk.surfaceMuted} ${dk.border} hover:border-emerald-400`}`} data-id="element-21256" data-mp="12bbve1c4o4tk">
                <div className={`font-bold text-sm mb-1 ${isSelected ? '' : dk.textPrimary}`} data-id="element-21257" data-mp="1he26f5bc0miz">
                  {ew.level}
                </div>
                <div className={`text-xs ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21258" data-mp="18dx2541fq491">
                  {ew.description}
                </div>
              </button>;
        })}
        </div>
      </div>
    </div>;
};
// ═══════════════════════════════════════════════════
// COMPACT CROSSOVER
// ═══════════════════════════════════════════════════
interface ContemporaryModuleCompactProps {
  subtype: string;
  setSubtype: (v: string) => void;
  conflictTypes: string[];
  setConflictTypes: React.Dispatch<React.SetStateAction<string[]>>;
  emotionalWeight: string;
  setEmotionalWeight: (v: string) => void;
}
export const ContemporaryModuleCompact: React.FC<ContemporaryModuleCompactProps> = ({
  subtype,
  setSubtype,
  conflictTypes,
  setConflictTypes,
  emotionalWeight,
  setEmotionalWeight
}) => {
  const dk = useDarkClasses();
  const toggleConflict = (c: string) => setConflictTypes(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  return <div className="space-y-5" data-id="element-21259" data-mp="1ynmbmpwnm7xi">
      <SingleSelectField label="Subtype" placeholder="Search subtypes..." items={CONTEMPORARY_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-21260" data-mp="1yoxgkt1u5lq7" />

      <SearchableChipField label="Conflict Type" placeholder="Search conflicts..." items={CONTEMPORARY_MODULE_DATA.conflictTypes} selected={conflictTypes} onToggle={toggleConflict} accentClass={ACCENT} data-id="element-21261" data-mp="2dkdb0z6bvjn4" />

      <div data-id="element-21262" data-mp="35a01w8yt86k4">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21263" data-mp="359gi0mpln83g">
          Emotional Weight
        </label>
        <div className="grid grid-cols-2 gap-2" data-id="element-21264" data-mp="ymraj1u1c3dw">
          {CONTEMPORARY_MODULE_DATA.emotionalWeights.map(ew => {
          const isSelected = emotionalWeight === ew.level;
          return <button key={ew.level} onClick={() => setEmotionalWeight(isSelected ? '' : ew.level)} className={`p-2.5 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-emerald-600 text-white border-emerald-600 dark:bg-emerald-400 dark:text-emerald-950' : `${dk.surfaceMuted} ${dk.border}`}`} data-id="element-21265" data-mp="18cki7rr23ycv">
                <div className={`font-bold text-xs mb-0.5 ${isSelected ? '' : dk.textPrimary}`} data-id="element-21266" data-mp="pmcmvvtlmc78">
                  {ew.level}
                </div>
                <div className={`text-[10px] ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21267" data-mp="kpm779lphimc">
                  {ew.description}
                </div>
              </button>;
        })}
        </div>
      </div>
    </div>;
};